import React from 'react';
import {
    Typography,
    Grid,
    Card,
    CardContent,
    Box,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/system';
import { CheckCircle, Repeat, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

// Styled components
const CarrierCard = styled(Card)(({ theme, selected }) => ({
    cursor: 'pointer',
    border: `2px solid ${selected ? theme.palette.primary.main : '#e5e7eb'}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: selected ? '0 4px 6px rgba(255, 56, 92, 0.1)' : 'none',
    transition: 'all 0.2s ease-in-out',
    transform: selected ? 'scale(1.02)' : 'scale(1)',
    height: '100%',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.08)',
    },
}));

const CurrentSubscriptionCard = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: '#FFF1F6',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #FCE7F3',
    position: 'relative',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
}));

const TransferBadge = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.main,
    borderRadius: '16px',
    padding: '4px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    width: 'fit-content',
}));

// Animation variants
const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 30,
            staggerChildren: 0.1
        }
    },
    exit: { opacity: 0, y: -20 }
};

const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: "spring",
            stiffness: 500,
            damping: 30
        }
    }
};

const CarrierSelection = ({ selectedCarrier, onSelectCarrier, existingSubscription }) => {
    const theme = useTheme();
    const carriers = ['Verizon', 'AT&T', 'Tmobile', 'Cricket'];

    // Render current subscription details
    const renderCurrentSubscription = () => {
        if (!existingSubscription) return null;

        return (
            <CurrentSubscriptionCard>
                <TransferBadge>
                    <Repeat size={14} />
                    Transfer Subscription
                </TransferBadge>

                <Typography variant="h6" fontWeight={600} gutterBottom>
                    {existingSubscription.display_name || existingSubscription.plan_name || "Your Current Plan"}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                            Current Carrier:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {existingSubscription.carrier ? existingSubscription.carrier.charAt(0).toUpperCase() + existingSubscription.carrier.slice(1) : "Unknown"}
                        </Typography>
                    </Grid>

                    <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                            Lines:
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                            {existingSubscription.lines || 1}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                mt: 1,
                                p: 1.5,
                                bgcolor: 'info.light',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Clock size={18} />
                                <Typography variant="body2">
                                    Your CircledIn subscription will remain active
                                </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight={600}>
                                Save ${existingSubscription.amount || "8.00"}/mo
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CurrentSubscriptionCard>
        );
    };

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
        >
            <Typography variant="h5" gutterBottom align="center" sx={{ mb: 2 }}>
                Switch Your Carrier
            </Typography>
            <Typography variant="body1" gutterBottom align="center" sx={{ mb: 4, maxWidth: '650px', mx: 'auto' }}>
                Keep your CircledIn subscription while switching to a different carrier. You'll only pay for the new carrier's activation fee and any shipping costs.
            </Typography>

            {renderCurrentSubscription()}

            <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3 }}>
                Select a new carrier:
            </Typography>

            <Grid container spacing={3}>
                {carriers.map((carrier) => (
                    <Grid item xs={12} sm={6} md={3} key={carrier}>
                        <motion.div variants={itemVariants}>
                            <CarrierCard
                                selected={selectedCarrier === carrier}
                                onClick={() => onSelectCarrier(carrier)}
                            >
                                <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                                    <Typography variant="h6" component="div" fontWeight={600}>
                                        {carrier}
                                    </Typography>
                                    {selectedCarrier === carrier && (
                                        <CheckCircle size={20} color={theme.palette.primary.main} style={{ marginTop: 8 }} />
                                    )}
                                </CardContent>
                            </CarrierCard>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>
        </motion.div>
    );
};

export default CarrierSelection;